import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton, printDocument } from '@/components/documents';
import { SearchSelect } from '@/components/SearchSelect';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  ScreenWithForm,
  SearchBar,
  Select,
  StatusPill,
  TextArea,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatCount, formatDate, formatQuantity } from '@/format';
import {
  useBirdsAlive,
  useBuyers,
  useDispatches,
  useLiveFlocks,
  useNextBookNo,
  useSalesMutation,
  type DispatchRow,
} from '@/hooks/sales';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';
import {
  STAGE_LABELS,
  averageWeightKg,
  draftProblems,
  netWeightKg,
  postingProblems,
  type DispatchDraft,
} from './dispatchRules';
import { VehicleField } from '@/components/VehicleField';

const PAGE = 50;

const STAGES = [
  { value: 'all', label: 'All' },
  { value: 'needsWeights', label: STAGE_LABELS.needsWeights },
  { value: 'needsPosting', label: STAGE_LABELS.needsPosting },
  { value: 'posted', label: STAGE_LABELS.posted },
  { value: 'reversed', label: STAGE_LABELS.reversed },
];

function emptyDraft(farmId: string): DispatchDraft & {
  vehicleNo: string;
  driverName: string;
  driverCnic: string;
  destination: string;
  crateCount: string;
  remarks: string;
} {
  return {
    farmId,
    flockId: '',
    buyerId: '',
    issuedOn: todayIso(),
    birdCount: '',
    bookGatePassNo: '',
    firstWeightKg: '',
    secondWeightKg: '',
    vehicleNo: '',
    driverName: '',
    driverCnic: '',
    destination: '',
    crateCount: '',
    remarks: '',
  };
}

type Draft = ReturnType<typeof emptyDraft>;

/**
 * Sales & Dispatch › Dispatch.
 *
 * The grid is the spreadsheet the farm office already keeps, in its own column
 * order — Date, Farm, Flock, Party, Book no, Gate pass no, Vehicle, Driver, First
 * weight, Second weight, Birds, Net weight, Avg weight, Note — so nobody has to
 * learn a new layout to read it.
 *
 * Two numbers, both shown. The book number is the serial off the paper book and is
 * what people quote to each other, so it leads; the gate pass number is the
 * server's and is what a bill and an audit row point at.
 */
export function Dispatch() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [stage, setStage] = useState('all');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DispatchRow | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(farmIds[0] ?? ''));
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const dispatches = useDispatches({ farmIds, stage, search, take: PAGE, skip });
  const flocks = useLiveFlocks(draft.farmId ? [draft.farmId] : farmIds);
  const buyers = useBuyers();
  const birdsAlive = useBirdsAlive(draft.flockId);
  const nextBookNo = useNextBookNo(draft.farmId, showForm && !editing);
  const mutate = useSalesMutation<unknown>();

  const rows = (dispatches.data?.items ?? []) as DispatchRow[];
  const total = dispatches.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  const canWeigh = can('sd.weighbridge.update');
  const canPost = can('sd.gatepass.post');
  const canReverse = can('sd.gatepass.reverse');

  const net = netWeightKg(draft.firstWeightKg, draft.secondWeightKg);
  const average = averageWeightKg(net, draft.birdCount);
  const alive = birdsAlive.data?.birdsAlive ?? null;
  const problems = useMemo(
    () => draftProblems(draft, { birdsAlive: alive, today: todayIso() }),
    [draft, alive],
  );
  const stillToDo = postingProblems(draft);

  const flockOptions = ((flocks.data?.items ?? []) as Array<{
    id: string;
    code: string;
    nameEn: string;
  }>).map((f) => ({ value: f.id, label: `${f.code} — ${f.nameEn}` }));

  const buyerOptions = (buyers.data?.items ?? []).map((b) => ({
    value: b.id,
    label: b.nameEn,
    // Omitted rather than set to undefined: `exactOptionalPropertyTypes` is on,
    // and the two are not the same type here.
    ...(b.overLimit ? { meta: 'over limit' } : {}),
  }));

  function startAdd() {
    setEditing(null);
    setDraft(emptyDraft(farmIds[0] ?? ''));
    setError('');
    setNote('');
    setShowForm(true);
  }

  function startEdit(row: DispatchRow) {
    setEditing(row);
    setDraft({
      farmId: row.farmId,
      flockId: row.flockId,
      buyerId: row.buyerId,
      issuedOn: row.issuedOn,
      birdCount: String(row.birdCount),
      bookGatePassNo: row.bookGatePassNo ?? '',
      firstWeightKg: row.firstWeightKg ?? '',
      secondWeightKg: row.secondWeightKg ?? '',
      vehicleNo: row.vehicleNo ?? '',
      driverName: row.driverName ?? '',
      driverCnic: '',
      destination: '',
      crateCount: row.crateCount === null ? '' : String(row.crateCount),
      remarks: row.remarks ?? '',
    });
    setError('');
    setNote('');
    setShowForm(true);
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /** The suggestion fills the box, and stays editable: the book is paper. */
  const suggestion = nextBookNo.data?.suggestion ?? null;

  async function save() {
    setError('');
    setNote('');
    if (Object.keys(problems).length > 0) {
      setError('Put the highlighted boxes right first');
      return;
    }

    const body = {
      farmId: draft.farmId,
      flockId: draft.flockId,
      buyerId: draft.buyerId,
      issuedOn: draft.issuedOn,
      birdCount: Number(draft.birdCount),
      ...(draft.bookGatePassNo.trim()
        ? { bookGatePassNo: draft.bookGatePassNo.trim() }
        : {}),
      ...(draft.vehicleNo.trim() ? { vehicleNo: draft.vehicleNo.trim() } : {}),
      ...(draft.driverName.trim() ? { driverName: draft.driverName.trim() } : {}),
      ...(draft.driverCnic.trim() ? { driverCnic: draft.driverCnic.trim() } : {}),
      ...(draft.destination.trim() ? { destination: draft.destination.trim() } : {}),
      ...(draft.crateCount.trim() ? { crateCount: Number(draft.crateCount) } : {}),
      ...(draft.remarks.trim() ? { remarks: draft.remarks.trim() } : {}),
    };

    try {
      const saved = editing
        ? await mutate.mutateAsync(() =>
            api.patch<DispatchRow>(`/api/v1/sd/dispatches/${editing.id}`, {
              version: editing.version,
              ...body,
            }),
          )
        : await mutate.mutateAsync(() =>
            api.post<DispatchRow>('/api/v1/sd/dispatches', body),
          );

      // The weights have their own permission and their own endpoint, because the
      // clerk who counts the birds and the operator who reads the bridge need not
      // be the same person.
      if (net !== null && canWeigh) {
        const row = saved as DispatchRow;
        await mutate.mutateAsync(() =>
          api.patch<DispatchRow>(`/api/v1/sd/dispatches/${row.id}/weights`, {
            version: row.version,
            firstWeightKg: draft.firstWeightKg,
            secondWeightKg: draft.secondWeightKg,
          }),
        );
      }

      setNote(editing ? 'Dispatch saved.' : 'Dispatch raised.');
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError(errorMessage(e, 'That dispatch could not be saved'));
    }
  }

  async function post(row: DispatchRow) {
    setError('');
    setNote('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/sd/dispatches/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.bookGatePassNo ?? row.documentNo} posted.`);
    } catch (e) {
      setError(errorMessage(e, 'That dispatch could not be posted'));
    }
  }

  async function remove(row: DispatchRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.delete(`/api/v1/sd/dispatches/${row.id}?version=${row.version}`),
      );
      setNote('Draft deleted.');
      setSelected(null);
    } catch (e) {
      setError(errorMessage(e, 'That draft could not be deleted'));
    }
  }

  function print(row: DispatchRow) {
    // Every printout in the app goes through this one helper, so the gate pass
    // carries the same letterhead and footer as a payslip or a stock document.
    printDocument({
      title: `Gate pass ${row.bookGatePassNo ?? row.documentNo}`,
      facts: [
        ['Book no', row.bookGatePassNo ?? '—'],
        ['Gate pass no', row.documentNo],
        ['Date', formatDate(row.issuedOn)],
        ['Farm', row.farmName],
        ['Flock', row.flockCode],
        ['Party', row.buyerName],
        ['Vehicle', row.vehicleNo ?? '—'],
        ['Driver', row.driverName ?? '—'],
        ['Birds', formatCount(row.birdCount)],
        ['Crates', row.crateCount === null ? '—' : formatCount(row.crateCount)],
        ['First weight', row.firstWeightKg ? `${row.firstWeightKg} kg` : '—'],
        ['Second weight', row.secondWeightKg ? `${row.secondWeightKg} kg` : '—'],
        ['Net weight', row.netKg ? `${row.netKg} kg` : '—'],
        ['Average weight', row.avgWeightKg ? `${row.avgWeightKg} kg` : '—'],
      ],
      showCost: false,
    });
    // Recorded as a PRINT in the audit log: who took a copy of a gate pass out of
    // the building is worth knowing.
    void api.get(`/api/v1/sd/dispatches/{id}/print`.replace('{id}', row.id) as never);
  }

  const columns: Array<GridColumn<DispatchRow>> = [
    { key: 'issuedOn', header: 'Date', width: 104, render: (r) => formatDate(r.issuedOn) },
    { key: 'farmName', header: 'Farm', width: 140, render: (r) => r.farmName },
    { key: 'flockCode', header: 'Flock', width: 110, render: (r) => r.flockCode },
    { key: 'buyerName', header: 'Party', width: 170, render: (r) => r.buyerName },
    {
      key: 'bookGatePassNo',
      header: 'Book no',
      width: 96,
      render: (r) => r.bookGatePassNo ?? <span className="muted">—</span>,
    },
    { key: 'documentNo', header: 'Gate pass no', width: 140, render: (r) => r.documentNo },
    {
      key: 'vehicleNo',
      header: 'Vehicle',
      width: 110,
      render: (r) => r.vehicleNo ?? <span className="muted">—</span>,
    },
    {
      key: 'driverName',
      header: 'Driver',
      width: 140,
      render: (r) => r.driverName ?? <span className="muted">—</span>,
    },
    {
      key: 'firstWeightKg',
      header: 'First weight',
      width: 110,
      align: 'end',
      render: (r) => (r.firstWeightKg ? formatQuantity(r.firstWeightKg, 3) : '—'),
    },
    {
      key: 'secondWeightKg',
      header: 'Second weight',
      width: 120,
      align: 'end',
      render: (r) => (r.secondWeightKg ? formatQuantity(r.secondWeightKg, 3) : '—'),
    },
    {
      key: 'birdCount',
      header: 'Birds',
      width: 90,
      align: 'end',
      render: (r) => formatCount(r.birdCount),
    },
    {
      key: 'netKg',
      header: 'Net weight',
      width: 110,
      align: 'end',
      render: (r) => (r.netKg ? formatQuantity(r.netKg, 3) : '—'),
    },
    {
      key: 'avgWeightKg',
      header: 'Avg weight',
      width: 110,
      align: 'end',
      render: (r) => (r.avgWeightKg ? formatQuantity(r.avgWeightKg, 3) : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => <StatusPill status={r.status} />,
    },
    {
      key: 'remarks',
      header: 'Note',
      width: 180,
      render: (r) => r.remarks ?? <span className="muted">—</span>,
    },
  ];

  const form = (
    <FormPanel
      title={editing ? `Dispatch ${editing.documentNo}` : 'Raise a dispatch'}
      icon={<AppIcon kind="outbound" size={18} />}
      intro={
        editing?.status === 'POSTED'
          ? 'This dispatch is posted. Only the book number can still be corrected — ' +
            'anything else needs a reversal.'
          : 'One row, the same as the sheet: who took the birds, which lorry, and what it weighed.'
      }
    >
      <FormGroup title="Who and where" columns={2}>
        <Select
          label="Farm"
          value={draft.farmId}
          onChange={(v) => {
            set('farmId', v);
            // The flock belongs to the farm, so changing the farm clears it rather
            // than leaving a flock from somewhere else selected.
            set('flockId', '');
          }}
          options={farmIds.map((id) => ({ value: id, label: id }))}
          required
          disabled={Boolean(editing)}
        />
        <Select
          label="Flock"
          value={draft.flockId}
          onChange={(v) => set('flockId', v)}
          options={flockOptions}
          required
          hint={problems.flockId}
          disabled={editing?.status === 'POSTED'}
        />
        <SearchSelect
          label="Party"
          value={draft.buyerId}
          onChange={(option) => set('buyerId', option?.value ?? '')}
          options={buyerOptions}
          selectedLabel={editing?.buyerName}
          required
          hint={problems.buyerId}
          disabled={editing?.status === 'POSTED'}
        />
        <Field
          label="Destination"
          value={draft.destination}
          onChange={(v) => set('destination', v)}
          placeholder="Lahore mandi"
          disabled={editing?.status === 'POSTED'}
        />
      </FormGroup>

      <FormGroup title="Numbers" columns={2}>
        <Field
          label="Book no"
          value={draft.bookGatePassNo}
          onChange={(v) => set('bookGatePassNo', v)}
          mono
          hint={
            suggestion && !draft.bookGatePassNo
              ? `The number printed in the gate pass book. Next looks like ${suggestion}.`
              : 'The number printed in the gate pass book.'
          }
        />
        <Field
          label="Gate pass no"
          value={editing?.documentNo ?? 'Given when you save'}
          onChange={() => undefined}
          mono
          disabled
          hint="The server's own number, used by bills and the audit trail."
        />
        <Field
          label="Date"
          type="date"
          value={draft.issuedOn}
          onChange={(v) => set('issuedOn', v)}
          required
          hint={problems.issuedOn}
          disabled={editing?.status === 'POSTED'}
        />
      </FormGroup>

      <FormGroup title="Vehicle and birds" columns={2}>
        <VehicleField
          label="Vehicle no"
          value={draft.vehicleNo}
          onChange={(v) => set('vehicleNo', v)}
          placeholder="BWP-1234"
          mono
          disabled={editing?.status === 'POSTED'}
        />
        <Field
          label="Driver name"
          value={draft.driverName}
          onChange={(v) => set('driverName', v)}
          disabled={editing?.status === 'POSTED'}
        />
        <Field
          label="Driver CNIC"
          value={draft.driverCnic}
          onChange={(v) => set('driverCnic', v)}
          mono
          disabled={editing?.status === 'POSTED'}
        />
        <Field
          label="Crates out"
          type="number"
          value={draft.crateCount}
          onChange={(v) => set('crateCount', v)}
          disabled={editing?.status === 'POSTED'}
        />
        <Field
          label="Birds"
          type="number"
          value={draft.birdCount}
          onChange={(v) => set('birdCount', v)}
          required
          hint={
            // Both, not one or the other: how many birds are in the shed is useful
            // before anything is typed, and stays useful while a figure is being
            // corrected. Showing only the complaint hides the number it is about.
            <>
              {alive !== null && (
                <span>Birds alive in this flock: {formatCount(alive)}</span>
              )}
              {problems.birdCount && (
                <span className="field__problem"> {problems.birdCount}</span>
              )}
            </>
          }
          disabled={editing?.status === 'POSTED'}
        />
      </FormGroup>

      {canWeigh && (
        <FormGroup
          title="Weights"
          hint="Enter the weights in the order the vehicle was weighed — the difference is the net."
          columns={2}
        >
          <Field
            label="First weight"
            type="number"
            value={draft.firstWeightKg}
            onChange={(v) => set('firstWeightKg', v)}
            disabled={editing?.status === 'POSTED'}
          />
          <Field
            label="Second weight"
            type="number"
            value={draft.secondWeightKg}
            onChange={(v) => set('secondWeightKg', v)}
            hint={problems.weights}
            disabled={editing?.status === 'POSTED'}
          />
          <Field
            label="Net weight"
            value={net === null ? '' : net}
            onChange={() => undefined}
            disabled
            mono
          />
          <Field
            label="Average weight"
            value={average === null ? '' : average}
            onChange={() => undefined}
            disabled
            mono
            hint="Net weight divided by birds. Never stored."
          />
        </FormGroup>
      )}

      <FormGroup title="Note">
        <TextArea
          label="Note"
          value={draft.remarks}
          onChange={(v) => set('remarks', v)}
          disabled={editing?.status === 'POSTED'}
        />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Save dispatch'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('sd.gatepass.create')}
      addLabel="Raise a dispatch"
      showForm={showForm}
      onToggle={() => (showForm ? setShowForm(false) : startAdd())}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <SearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setSkip(0);
          }}
          placeholder="Search book no, gate pass, vehicle, driver or party"
        />
        <Select
          label="Stage"
          value={stage}
          onChange={(v) => {
            setStage(v);
            setSkip(0);
          }}
          options={STAGES}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(current) && can('sd.gatepass.update'),
            reason: 'Pick a dispatch first',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Post',
            icon: 'post',
            primary: true,
            enabled: Boolean(current && current.status === 'DRAFT' && canPost),
            reason:
              current?.status === 'DRAFT'
                ? 'You cannot post a dispatch'
                : 'Only a draft can be posted',
            onSelect: () => current && void post(current),
          },
          {
            label: 'Print',
            icon: 'print',
            enabled: Boolean(current) && can('sd.gatepass.print'),
            reason: 'Pick a dispatch first',
            onSelect: () => current && print(current),
          },
          {
            label: 'Delete draft',
            icon: 'remove',
            danger: true,
            enabled: Boolean(
              current && current.status === 'DRAFT' && can('sd.gatepass.delete'),
            ),
            reason: 'Only a draft can be deleted',
            onSelect: () => current && void remove(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && canReverse && (
          <ReverseButton
            label={`Reverse ${current.bookGatePassNo ?? current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/sd/dispatches/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Dispatch reversed.'))
                .catch((e: unknown) =>
                  setError(errorMessage(e, 'That dispatch could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="sd-dispatch"
        label="Dispatches"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => startEdit(row)}
        empty="No dispatches yet."
        loading={dispatches.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        {stillToDo.length > 0 && showForm ? (
          <span>{stillToDo.join(' · ')}</span>
        ) : (
          <span>
            {formatCount(total)} dispatch{total === 1 ? '' : 'es'}
          </span>
        )}
      </StatusBar>
    </ScreenWithForm>
  );
}
